import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { analyticsApi } from '../services/analytics-api.service';
import type {
  FAClientsQueryParams,
  SortOrder,
} from '../types/analytics.types';

const DEBOUNCE_MS = 300;

export const faClientsKeys = {
  all: ['analytics', 'fa-clients'] as const,
  list: (params?: Omit<FAClientsQueryParams, 'cursor'>) =>
    [...faClientsKeys.all, 'list', params] as const,
};

type FAClientsBaseParams = {
  partnerCompanyId?: string;
  excludeInternal?: boolean;
};

export const useFAClientsData = (baseParams?: FAClientsBaseParams, enabled = true) => {
  const [search, setSearchRaw] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [sortBy, setSortBy] = useState<string>('totalClients');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showPendingInvitations, setShowPendingInvitations] = useState(false);

  const setSearch = useCallback(
    (value: string) => {
      setSearchRaw(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => setDebouncedSearch(value), DEBOUNCE_MS);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const queryParams = useMemo<Omit<FAClientsQueryParams, 'cursor'>>(() => {
    const params: Omit<FAClientsQueryParams, 'cursor'> = {
      sortBy,
      sortOrder,
      limit: 50,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (baseParams?.partnerCompanyId)
      params.partnerCompanyId = baseParams.partnerCompanyId;
    if (baseParams?.excludeInternal !== undefined)
      params.excludeInternal = baseParams.excludeInternal;
    if (showPendingInvitations) params.showPendingInvitations = true;
    return params;
  }, [sortBy, sortOrder, debouncedSearch, baseParams?.partnerCompanyId, baseParams?.excludeInternal, showPendingInvitations]);

  const query = useInfiniteQuery({
    queryKey: faClientsKeys.list(queryParams),
    queryFn: async ({ pageParam }) => {
      const response = await analyticsApi.getFAClients({
        ...queryParams,
        cursor: pageParam ?? undefined,
      });
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const allAdvisors = useMemo(
    () => query.data?.pages.flatMap((page) => page.advisors) ?? [],
    [query.data?.pages],
  );

  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  const handleSort = useCallback(
    (column: string) => {
      if (column === sortBy) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(column);
        setSortOrder('desc');
      }
    },
    [sortBy],
  );

  return {
    advisors: allAdvisors,
    totalCount,
    search,
    setSearch,
    sortBy,
    sortOrder,
    handleSort,
    showPendingInvitations,
    setShowPendingInvitations,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
